import {
    isUserLoggedIn,
    getURLParameter,
    scrollToTop,
    simulateClick,
    unblockScroll,
    blockScroll,
} from "../utilities/Helpers/helpers";
import WishlistAPI from "../utilities/Wishlist/WishlistAPI";
import AddToWishlistModal from "../utilities/Wishlist/Modals/AddToWishlistModal";
import { handlers } from "../utilities/Wishlist/WishlistHandles";
import { wishlistHelpers } from "../utilities/Wishlist/WishlistHelpers";
import SwatchHelper from "../utilities/Helpers/UI/SwatchHelper";
import ChangeSizeModal from "../utilities/Wishlist/Modals/ChangeSizeModal";
import Alpine from "alpinejs";

const wishlistInit = {
    // Main configure
    alpine: true,
    wishlistApi: null,
    addToWishlistModal: null,
    changeSizeModal: null,
    // Management state
    loader: false,
    isDetail: false,
    activeWishlistItem: {},
    wishlists: [],
    migratableWishlists: [],
    variantsAddedToWishlists: [],
    currentWishlist: null,
    activeWishlistInModal: null,
    selectedVariant: null,
    // Static
    sharedLink: "",
    //x-models
    valid: false,
    variantsChecked: [],
    newWishlistName: "",
    async init() {
        this.closePopups();
        this.addToWishlistModal = AddToWishlistModal.getModalByName("wishlist");
        this.changeSizeModal = ChangeSizeModal.getModalByName("change-size");
        this.changeSizeModal.onOpened = () => blockScroll();
        this.changeSizeModal.onClosed = () => unblockScroll();
        this.wishlistApi = new WishlistAPI();
        await this.initWishlistToMigrate();
        await this.getWishlistsToDashboard();
        this.initCurrentWishlistIfIsDetail();
        this.setIsDetail(this.currentWishlist !== null);
        this.initializeWishlistLink();
        this.refreshWishlistVariants();
        this.swatchClickHandler();
    },

    async getWishlistsToDashboard() {
        this.loader = true;
        if (this.isShared()) {
            await this.getSharedWishlistsToDashboard();
        } else {
            await this.reinitializeWishlists();
        }
        this.backButtonHandler();
        this.loader = false;
    },

    async getSharedWishlistsToDashboard() {
        await this.wishlistApi.getWishlist(
            getURLParameter("wishlist_id"),
            (wishlist) => {
                this.wishlists.push(wishlist);
            },
            (exception) => {}
        );
    },

    async removeVariant(variantId, wishlistId = null, reinitializeWishlist = true) {
        wishlistId = wishlistId ? wishlistId : this.currentWishlist?.id;
        if (wishlistId) {
            await this.wishlistApi.removeVariant(
                wishlistId,
                variantId,
                async (response) => {
                    this.hideDropdowns();
                    if (reinitializeWishlist) {
                        await this.reinitializeWishlists();
                    }
                    this.backToDashboardIfWishlistIsEmpty(wishlistId);
                },
                (exception) => {}
            );
        }
    },

    async addVariant(variantId = null, productId = null, wishlistId = null) {
        let jsonData = {};
        let key = variantId ? "variant_id" : "product_id";
        jsonData[key] = variantId ? variantId : productId;
        wishlistId = wishlistId ? wishlistId : this.currentWishlist?.id;
        if (wishlistId) {
            await this.wishlistApi.addVariantToWishlist(
                wishlistId,
                jsonData,
                (response) => {},
                (exception) => {}
            );
        }
    },

    async reinitializeWishlists() {
        await this.wishlistApi.getWishlists(
            (wishlists) => {
                this.wishlists = wishlists;
                if (this.currentWishlist) {
                    this.setCurrentWishlist();
                }
            },
            (exception) => {}
        );
    },

    async createWishlist(successCallback) {
        if (this.valid) {
            const jsonData = {
                label: this.newWishlistName,
            };
            await this.wishlistApi.createWishlist(jsonData, (response) => {
                successCallback(response);
                this.newWishlistName = "";
                this.valid = false;
            });
        }
    },

    async initWishlistToMigrate() {
        if (isUserLoggedIn()) {
            this.migratableWishlists = [];
            await this.wishlistApi.getWishlists(
                (wishlists) => {
                    if (wishlists.length >= 1) {
                        wishlists.forEach((list) => {
                            this.migratableWishlists.push(list.id);
                        });
                    }
                },
                () => {},
                this.wishlistApi.apiHelper.getApiAnonToken("wishlist_anon_token")
            );
        }
    },

    isShared() {
        return parseInt(getURLParameter("share")) === 1 && getURLParameter("wishlist_id") !== false;
    },

    getWishlistById(wishlistId) {
        return this.wishlists.find((wishlist) => wishlist.id === parseInt(wishlistId));
    },

    initCurrentWishlistIfIsDetail() {
        if (getURLParameter("wishlist_id")) {
            this.setCurrentWishlist(getURLParameter("wishlist_id"));
        }
    },

    setCurrentWishlist(wishlistId = null, setIsDetail = true) {
        if (!wishlistId) {
            this.currentWishlist = this.wishlists.find((wishlist) => wishlist.active);
        } else {
            this.currentWishlist = this.getWishlistById(wishlistId);
        }
        if (setIsDetail) {
            this.setIsDetail(setIsDetail);
        }
    },

    removeCurrentWishlist() {
        this.currentWishlist = null;
        this.setIsDetail(false);
    },

    getVariantFromCurrentWishlistById(variantId) {
        return this.currentWishlist.variants.find((variant) => variant.id === parseInt(variantId));
    },

    setIsDetail(isDetail) {
        this.isDetail = isDetail;
    },

    refreshWishlistVariants() {
        this.variantsAddedToWishlists = Array.from(
            new Set(
                this.wishlists.flatMap((wishlist) =>
                    wishlist.variants.map((variant) => {
                        return {
                            variantId: parseInt(variant.id),
                            productId: parseInt(variant.productId),
                        };
                    })
                )
            )
        );
    },

    verifyVariantInWishlists(variantId = null, productId = null) {
        return this.variantsAddedToWishlists.some(
            (item) => item.variantId === parseInt(variantId) || item.productId === parseInt(productId)
        );
    },

    selectVariant(variantId) {
        this.selectedVariant = this.currentWishlist.variants.find((variant) => variant.id === parseInt(variantId));
    },

    setAllWishlistsActive() {
        this.wishlists.forEach((wishlist) => {
            wishlist.setActive(true);
        });
    },

    /**
     * To remove after implement new swatches
     */
    swatchClickHandler() {
        document.addEventListener("vsk:product:changed", (event) => {
            const productItem = event.target.closest("[swatch-generated]");
            const wishlistElement = productItem.querySelector(
                "[data-js-wishlist-add-product][data-js-wishlist-add-variant]"
            );
            wishlistElement.dataset.jsWishlistAddProduct = event.detail.productID;
            wishlistElement.dataset.jsWishlistAddVariant = "";
            this.refreshWishlistVariants();
        });
    },

    removeWishlist(wishlistId) {
        this.wishlistApi.removeWishlist(wishlistId, async () => {
            this.hideDropdowns();
            await this.reinitializeWishlists();
            scrollToTop(null, 0);
            const backToWishlistsListButton = document.querySelector("[data-js-wishlist-button-back]");
            if (backToWishlistsListButton && backToWishlistsListButton.classList.contains("active")) {
                simulateClick(backToWishlistsListButton);
            }
        });
    },

    backToDashboardIfWishlistIsEmpty(wishlistId) {
        if (!wishlistId) {
            return;
        }
        const wishlist = document.querySelector('[data-js-wishlist-products="' + wishlistId + '"]');
        if (wishlist) {
            const products = wishlist.querySelectorAll("[data-js-wishlist-product]");
            if (products.length < 1) {
                this.backToWishlistsList();
            }
        }
    },

    reinitializeWishlistListView() {
        this.setAllWishlistsActive();
        this.removeCurrentWishlist();
        scrollToTop(null, 0);
    },

    backButtonHandler() {
        window.addEventListener("popstate", (event) => {
            const wishlistId = getURLParameter("wishlist_id");
            if (wishlistId) {
                const wishlist = document.querySelector('[data-js-wishlist-id="' + wishlistId + '"]');
                const wishlistElement = wishlist.querySelector("[data-js-wishlist-show-products]");
                if (wishlistElement) {
                    simulateClick(wishlistElement);
                }
            } else {
                this.reinitializeWishlistListView();
            }
        });
    },

    updateWishlistsForVariant() {
        this.wishlists.forEach((wishlist) => {
            if (wishlist.variants.length > 0) {
                let variant = wishlist.variants.find(
                    (variant) =>
                        this.activeWishlistItem.variantId === parseInt(variant.id) ||
                        this.activeWishlistItem.productId === parseInt(variant.productId)
                );

                if (typeof variant !== "undefined") {
                    this.activeWishlistItem.variantId = variant.id;
                    this.addToWishlistModal.wishlistsInVariantCurrentState.push(wishlist.id);
                }
            }
        });
        this.addToWishlistModal.wishlistsInVariantNewState =
            this.addToWishlistModal.wishlistsInVariantCurrentState.slice();
    },

    shareWishlist(wishlistId) {
        const jsonData = {
            shared: "1",
        };
        this.wishlistApi.updateWishlist(
            wishlistId,
            jsonData,
            (response) => {},
            (exception) => {},
            true
        );
    },

    editWishlist(successCallback, wishlistId = null) {
        const jsonData = {
            label: this.newWishlistName,
        };
        wishlistId = wishlistId ? wishlistId : this.currentWishlist?.id;
        this.wishlistApi.updateWishlist(
            wishlistId,
            jsonData,
            (response) => {
                successCallback(response);
            },
            (exception) => {}
        );
    },

    changeProductVariant(options) {
        const jsonData = {
            options: options,
        };
        this.wishlistApi.updateProductVariantInWishlist(
            this.currentWishlist.id,
            this.selectedVariant.id,
            jsonData,
            async (response) => {
                await this.reinitializeWishlists();
                ChangeSizeModal.getModalByName("change-size").close();
                unblockScroll();
            },
            (exception) => {}
        );
    },
};

export const wishlistStore = {
    ...wishlistInit,
    ...wishlistHelpers,
    ...handlers,
};
